import { FlightCheckDoneIcon, FormFieldLabel, networkIconFor } from '@alison/ui';
import OpenInNewOutlined from '@mui/icons-material/OpenInNewOutlined';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { useCreativeThumbnails, usePlatformsById } from '../../lib/workflows.hooks.js';
import { CreativeGrid } from './CreativeGrid.js';
import { AttributeBar, LABEL_SX, readable, ScoreRing, toneOf } from './ScoreVisuals.js';
import {
  ATTENTION_BELOW,
  batchName,
  requestedScoringLine,
  type ScoredBatchPlatform,
  scoredCreatives,
  scoredPlatforms,
  whenCompleted,
} from './scoredBatch.js';
import { useLatestBatch } from './useCreativeDrawer.js';

const RING_SIZE = 168;

const CARD_SX = {
  gap: 4,
  p: 4,
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

const PLATFORM_SX = {
  gap: 1.5,
  minWidth: 0,
  alignItems: 'center',
  px: 3,
  py: 2,
  borderRadius: 1.5,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  cursor: 'pointer',
  textAlign: 'left',
  width: '100%',
};

const PLATFORM_ON_SX = { borderColor: 'text.primary' };

export interface ScoredBatchViewProps {
  result: unknown;
  runId?: string | null;
  completedAt?: string | null;
  onOpenFullAnalysis?: () => void;
  onOpenCreative?: (creativeId: string, networkId: number) => void;
}

function Counted({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number;
  hint: string;
  tone?: string;
}): ReactNode {
  return (
    <Stack sx={{ gap: 0.25 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: 30, fontWeight: 600, lineHeight: 1.2, color: tone }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: 12, color: 'text.muted' }}>{hint}</Typography>
    </Stack>
  );
}

export function ScoredBatchView({
  result,
  runId,
  completedAt,
  onOpenFullAnalysis,
  onOpenCreative,
}: ScoredBatchViewProps): ReactNode {
  const theme = useTheme();
  const known = usePlatformsById();
  const media = useCreativeThumbnails(runId ?? null);
  const { runs, latest } = useLatestBatch(runId ?? null, result);
  const platformOf = (networkId: number): { slug: string; label: string } =>
    known[networkId] ?? { slug: '', label: String(networkId) };
  const platforms = scoredPlatforms(latest, platformOf);
  const [openId, setOpenId] = useState<number | null>(null);
  const open: ScoredBatchPlatform | undefined =
    platforms.find((platform) => platform.networkId === openId) ?? platforms[0];

  if (open === undefined) return null;

  const name = batchName(latest);
  const files = new Set(scoredCreatives(latest).map((creative) => creative.creativeId)).size;
  const finished = whenCompleted(completedAt);
  const done = theme.palette.score.high.main;

  return (
    <Stack sx={{ gap: 3 }}>
      <Stack
        direction="row"
        sx={{
          gap: 1.5,
          alignItems: 'center',
          px: 3,
          py: 2,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: alpha(done, 0.2),
          bgcolor: alpha(done, 0.1),
        }}
      >
        <FlightCheckDoneIcon sx={{ fontSize: 20, color: done }} />
        <Stack sx={{ gap: 0.25 }}>
          <FormFieldLabel sx={{ ...LABEL_SX, color: 'text.muted' }}>
            {name === null
              ? 'FLIGHT CHECK COMPLETE'
              : `${name.toUpperCase()} – FLIGHT CHECK COMPLETE`}
          </FormFieldLabel>
          <Typography sx={{ fontSize: 13 }}>
            {files} creative{files === 1 ? '' : 's'} · Saved to Outcomes
          </Typography>
        </Stack>
      </Stack>

      <Stack sx={CARD_SX}>
        <Stack
          direction="row"
          sx={{ gap: 2, alignItems: 'flex-start', justifyContent: 'space-between' }}
        >
          <Stack sx={{ gap: 0.25 }}>
            {name === null ? null : (
              <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{name}</Typography>
            )}
            {finished === null ? null : (
              <Typography sx={{ fontSize: 12, color: 'text.muted' }}>{finished}</Typography>
            )}
          </Stack>
          {onOpenFullAnalysis === undefined ? null : (
            <Link
              component="button"
              type="button"
              onClick={onOpenFullAnalysis}
              sx={{ display: 'flex', gap: 1, alignItems: 'center', fontSize: 13, fontWeight: 600 }}
            >
              Open full report
              <OpenInNewOutlined sx={{ fontSize: 16 }} />
            </Link>
          )}
        </Stack>

        <Stack direction="row" sx={{ gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <Stack sx={{ gap: 3, minWidth: 180 }}>
            <Counted
              label="Creatives analyzed"
              value={open.analysed}
              hint={open.scoredBy ?? requestedScoringLine(latest) ?? `Scored on ${open.label}`}
            />
            <Divider />
            <Counted
              label="Needs attention"
              value={open.needsAttention}
              hint={`Creatives scored under ${ATTENTION_BELOW.toFixed(1)}`}
              {...(open.needsAttention > 0 ? { tone: theme.palette.score.low.main } : {})}
            />
          </Stack>

          <ScoreRing score={open.score} size={RING_SIZE} />

          <Stack sx={{ gap: 1.5, flex: 1, minWidth: 300 }}>
            {open.attributes.map((attribute) => (
              <AttributeBar
                key={attribute.name}
                name={attribute.name}
                percent={attribute.percent}
              />
            ))}
          </Stack>
        </Stack>

        {platforms.length > 1 ? (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: 'repeat(auto-fit, minmax(190px, 1fr))',
                md: `repeat(${platforms.length}, minmax(0, 1fr))`,
              },
            }}
          >
            {platforms.map((platform) => {
              const Icon = networkIconFor(platform.slug, platform.label);
              return (
                <Stack
                  key={platform.networkId}
                  component="button"
                  type="button"
                  direction="row"
                  aria-pressed={platform.networkId === open.networkId}
                  onClick={() => setOpenId(platform.networkId)}
                  sx={[PLATFORM_SX, platform.networkId === open.networkId && PLATFORM_ON_SX]}
                >
                  {Icon === undefined ? null : <Icon sx={{ fontSize: 20 }} />}
                  <Typography sx={{ fontSize: 14, flex: 1 }}>{platform.label}</Typography>
                  <Typography
                    sx={{ fontSize: 20, fontWeight: 500, color: toneOf(theme, platform.score) }}
                  >
                    {readable(platform.score)}
                  </Typography>
                </Stack>
              );
            })}
          </Box>
        ) : null}
      </Stack>

      <CreativeGrid
        result={latest}
        platforms={platforms}
        media={media}
        runs={runs}
        {...(onOpenCreative ? { onOpenCreative } : {})}
      />
    </Stack>
  );
}
